import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/utils/cn";
import { useScreenType } from "@/lib/screenType";

/**
 * The surface a card is painted with. **W43 `ADR-0109` withdrew the pale tier.**
 *
 * W42's default was `tint` — the pale fill on every secondary card and list row.
 * In a mockup that reads as a palette. On a real device eleven cards each on a
 * faintly pink ground read as a colour cast over the whole page, and that is the
 * observation the withdrawal is based on. White is the default now; the pale tone
 * is spent **once per screen**, on the mascot hero.
 *
 * - `plain`  — white. The default, and what most of the app is.
 * - `hero`   — the pale mascot card. At most one per screen.
 * - `accent` — a saturated `surface-*` block. At most `MAX_ACCENT_CARDS` per
 *              screen, and the colour comes from `ACCENT_SLOTS`, not from here.
 * - `glass`  — translucent white + blur, the floating layer. The only tier that
 *              adds a `backdrop-filter`.
 */
export type CardSurface = "plain" | "hero" | "accent" | "glass";

const SURFACE_CLASS: Record<CardSurface, string> = {
  plain: "surface-plain",
  hero: "surface-hero",
  accent: "", // the fill comes from the slot's own `surface-*`
  glass: "surface-glass",
};

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Which element to render. `div` by default.
   *
   * W53.2 `ADR-0182`. Added because three of the detail pages' cards are an
   * `<article>`, a `<section>` and a `<article>` — and before it the only ways to
   * keep the semantics were to hand-write the surface classes (which is how
   * `TimelineEventCard` and `AnniversaryDiaryList` ended up with no shadow at
   * all) or to nest a `div` inside a semantic element. `HeroCard` and
   * `AccentCard` in `components/primitives/HeroCard.tsx` already carry this prop,
   * so this follows an existing pattern rather than inventing one.
   *
   * `a` is deliberately **not** here: a navigable card is a react-router
   * `<Link>`, not an `<a href>`, and putting that branch here would mean
   * re-implementing client-side navigation to satisfy a type. Those hosts wrap a
   * `<Card>` and put the link inside it — see `TimelineEventCard`.
   *
   * ### `data-component` is overridable, and that is load-bearing
   *
   * `{...rest}` is spread **last**, so a host passing its own `data-component`
   * replaces the `"Card"` default. That is how `TimelineEventCard` keeps
   * `[data-component='TimelineEventCard'][data-state='future']` — a selector
   * five e2e specs and `scripts/shoot-w45-live.mjs` depend on by name. Dropping
   * it during this round's refactor would have broken all of them silently,
   * because none of them asserts anything about the component that answers the
   * selector.
   */
  as?: "div" | "article" | "section";
  /** Bump elevation to modal-shadow variant (used inside Popovers / BottomSheets). */
  elevated?: boolean;
  /**
   * Deeper background, for filter / banner contexts.
   *
   * Kept for API compatibility (`component-api.md` §1 is additive-only, so the
   * prop cannot be removed) and it only affects `plain` and `glass`, where there
   * is a second neutral to step down to. On `hero` / `accent` it is a no-op: a
   * "deeper" fill there would be a new colour value, and an unmeasured colour
   * value is exactly what the contrast gate exists to prevent.
   */
  deep?: boolean;
  /** Surface tier. Defaults to `plain` — white. */
  surface?: CardSurface;
  /**
   * Paint no surface at all, so whatever hosts this card shows through.
   *
   * W43 `ADR-0109`. This is a prop rather than a `bg-transparent` class on
   * purpose: `surface-plain` is an **unlayered** CSS rule and Tailwind
   * utilities live in `@layer utilities`, so `bg-transparent` on the element
   * loses and the card stays white. That is the same specificity trap the
   * nav's 34px circle fell into, one axis over, and the first version of this
   * fix walked straight into it — which is why `HeroCard.tsx`'s exported
   * `BARE_CARD` string is **deleted** as of W45.0 `ADR-0113` rather than left
   * as an alternative spelling of a thing that does not work.
   *
   * W45.0: the contract is **fill + border + shadow**, all three. See the note
   * at the call site for why the shadow was the one that mattered.
   */
  bare?: boolean;
}

/**
 * Card — base surface + 1-step shadow.
 *
 * Per `DESIGN.md §6.1`: hairline-first, 2-step shadow (not Material's
 * 4-step elevation scale). `elevated` swaps to `shadow-modal` for popovers.
 *
 * Per Apple HIG utility-card pattern (refactor 2026-09-22):
 *   - 18px radius (`rounded-2xl`) instead of `rounded-md` 12px
 *   - Default section padding bumped to `p-6` (24px) for App-level consistency
 *
 * ### The radius is 18px here and 16px on the hero, and that is a decision
 *
 * W53.2 `ADR-0180`. Two values survive this round — `rounded-2xl` 18px on this
 * component, `rounded-lg` 16px on `HeroCard` / `AccentCard` — so it is worth
 * saying why they are two and not one, because "two radii in the same system"
 * is what D-3 was filed about.
 *
 * They are keyed by **role**, not drifting: the hero is a wide saturated or pale
 * block whose edge is defined by a value difference against the page wash, and
 * `DESIGN.md:545` specifies `lg` 16 with a written reason. This card is a white
 * block on a white-ish ground, separated by a hairline and an 8/16px shadow
 * pair — a larger radius on a shadowless-looking edge is what softens the
 * corner past the point the hairline can carry. `DESIGN.md` §「圓角」 now states
 * both, so the pair is declared in one place instead of being re-derived per
 * component.
 *
 * What is **not** allowed any more is a third value that is not one of these
 * two — which is what `rounded-xl` was: 12px under a name that is not in the
 * scale, in 14 places, invisible to `hard-rule-audit.mjs` because Tailwind
 * considers it a valid default.
 *
 * ### Why it reads the screen type even though the default is white
 *
 * W42 hard-coded `bg-paper-elevated`, which made all 39 routes white by default
 * and left the saturated tokens reachable only by a component asking for one by
 * hand — three did. The route audit's finding was `surface-coral` 0 components,
 * `chrome-navy` 0 components, 39 routes white.
 *
 * Reading the type from context rather than taking it as a prop is what stops
 * that recurring: a `screenType` prop would be opt-in with an extra step, and the
 * next route added would omit it. `ScreenTypeProvider` sits at the AppShell root,
 * so one declaration covers all 39.
 *
 * `useScreenType()` defaults to `data` with no provider mounted, so this still
 * renders in the isolated unit tests that never build a router. Both values are
 * also written to `data-*` so a test can assert the resolution on the element
 * itself, with no stylesheet and no cascade — jsdom has no layout engine, and the
 * CSS that actually paints the colour is exactly the part it cannot evaluate.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { as: Tag = "div", elevated, deep, surface, bare = false, className, ...rest },
  ref,
) {
  const screenType = useScreenType();
  const resolved: CardSurface = surface ?? "plain";
  const deepApplies = deep === true && !bare && (resolved === "plain" || resolved === "glass");

  return (
    <Tag
      ref={ref}
      data-component="Card"
      data-surface={bare ? "bare" : resolved}
      data-screen-type={screenType}
      className={cn(
        "rounded-2xl border border-hairline",
        // W45.0 `ADR-0113`. **bare means no surface at all** — no fill, no
        // border, **no shadow**. Radius and padding stay, because those are
        // layout rather than surface.
        //
        // The shadow was the part that was left behind. `bare` dropped the fill
        // and the border and kept `rounded-2xl` plus `shadow-card`, and a
        // transparent rounded box with an 8px/16px shadow on a saturated lime
        // field is exactly the inner rounded rectangle reported as 「卡中卡」 on
        // the dashboard. The fill had already been removed, so an audit that read
        // the source reported it fixed while the screenshot said otherwise.
        //
        // It was also an **inconsistency**, not a style choice: the two sibling
        // cards on the same screen (`DailyAnniversaryCard`, `AnniversaryRitual`)
        // already dropped all three. Those two inline the string rather than
        // importing it and they keep working, because their bare branches have
        // no `surface-*` rule competing — which is luck, not a contract.
        bare
          ? "border-0 shadow-none"
          : cn(
              SURFACE_CLASS[resolved],
              // `deep` is a no-op on `bare`: there is no fill to deepen.
              deepApplies && "bg-paper-deep",
              elevated ? "shadow-modal" : "shadow-card",
            ),
        className,
      )}
      {...rest}
    />
  );
});

export interface CardSectionProps extends HTMLAttributes<HTMLDivElement> {
  pad?: "sm" | "md" | "lg";
}

export const CardSection = forwardRef<HTMLDivElement, CardSectionProps>(function CardSection(
  { pad = "md", className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(pad === "sm" && "p-4", pad === "md" && "p-6", pad === "lg" && "p-8", className)}
      {...rest}
    />
  );
});
